"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface PlanProps {
  title: string;
  popular: boolean;
  monthlyPrice: number;
  annualPrice: number;
  buttonLabel?: string;
  features: string[];
  monthlyPriceId: string;
  annualPriceId: string;
  description: string;
}

const pricingPlans: PlanProps[] = [
  {
    title: "Starter",
    popular: false,
    monthlyPrice: 100,
    annualPrice: 200,
    monthlyPriceId: "",
    annualPriceId: "",
    buttonLabel: "Purchase Now",
    description:
      "Lorem ipsum dolor sit, amet ipsum consectetur adipisicing elit.",
    features: [
      "SEO Strategy & Topic Recommendations",
      "Competitor Analysis to stand out",
      "Built-in Keyword Research",
      "Target latest Google trends",
      "SEO optimized blogs and socials",
      "Technical SEO analysis and Reports",
      "Target 100+ regions and languages",
    ],
  },
  {
    title: "Professional",
    popular: true,
    monthlyPrice: 200,
    annualPrice: 2000,
    monthlyPriceId: "",
    annualPriceId: "",
    buttonLabel: "Purchase Now",
    description:
      "Lorem ipsum dolor sit, amet ipsum consectetur adipisicing elit.",
    features: [
      "Everything in Basic plan",
      "Get 25 premium blogs",
      "Index up to 1000 pages",
      "Premium support",
      "Local SEO",
      "SEO Agent",
    ],
  },
  {
    title: "Premium",
    popular: false,
    monthlyPrice: 200,
    annualPrice: 2000,
    monthlyPriceId: "",
    annualPriceId: "",
    buttonLabel: "Purchase Now",
    description:
      "Lorem ipsum dolor sit, amet ipsum consectetur adipisicing elit.",
    features: [
      "Everything in Professional plan",
      "Get Unlimited premium blogs",
      "Add your own AI Model key",
      "Premium support & training sessions",
    ],
  },
];

export default function Pricing05() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    "annual"
  );
  const Heading = () => (
    <div className="flex items-center justify-center gap-3">
      <button
        onClick={() => setBillingCycle("monthly")}
        className={cn(
          `relative rounded-lg px-4 py-2 text-sm font-medium`,
          billingCycle === "monthly"
            ? "bg-primary text-primary-foreground"
            : "text-foreground hover:bg-accent hover:text-accent-foreground"
        )}
      >
        Monthly
        {billingCycle === "monthly" && <BackgroundShift shiftKey="monthly" />}
      </button>
      <button
        onClick={() => setBillingCycle("annual")}
        className={cn(
          `relative rounded-lg px-4 py-2 text-sm font-medium`,
          billingCycle === "annual"
            ? "bg-primary text-primary-foreground"
            : "text-foreground hover:bg-accent hover:text-accent-foreground"
        )}
      >
        Annual
        {billingCycle === "annual" && <BackgroundShift shiftKey="annual" />}
      </button>
    </div>
  );

  const PricingCard = ({ plan }: { plan: PlanProps }) => (
    <div
      className={`w-full rounded-xl border border-border p-6 text-left ${
        plan.popular
          ? "bg-black text-white border-primary"
          : "bg-card text-foreground"
      }`}
    >
      <p className="mb-1 mt-0 text-sm font-medium uppercase text-primary">
        {plan.title}
      </p>
      <p className="my-0 mb-6 text-sm">{plan.description}</p>
      <div className="mb-8 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p
            key={billingCycle === "monthly" ? "monthly" : "annual"}
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="my-0 text-3xl font-semibold"
          >
            <span>
              $
              {billingCycle === "monthly"
                ? plan.monthlyPrice
                : plan.annualPrice}
            </span>
            <span className="text-sm font-medium">
              /{billingCycle === "monthly" ? "month" : "year"}
            </span>
          </motion.p>
        </AnimatePresence>
        <motion.div className="mt-7" whileTap={{ scale: 0.985 }}>
          <Button variant="default" className="w-full !py-5">
            {plan.buttonLabel || "Get Started"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 text-white" />
          </Button>
        </motion.div>
      </div>
      {plan.features.map((feature, idx) => (
        <div key={idx} className="mb-3 flex items-center gap-2">
          <Check className="text-primary" size={18} />
          <span className="text-sm">{feature}</span>
        </div>
      ))}
    </div>
  );

  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>PRICING</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Get More Traffic Now
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Give your product the visibility it deserves
        </p>
      </div>
      <Heading />
      <div className="mt-12">
        <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-8 lg:flex-row lg:gap-4">
          {pricingPlans.map((plan, index) => (
            <PricingCard plan={plan} key={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

const BackgroundShift = ({ shiftKey }: { shiftKey: string }) => (
  <motion.span
    key={shiftKey}
    layoutId="bg-shift"
    className="absolute inset-0 -z-10 rounded-lg bg-primary"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ type: "spring", stiffness: 200, damping: 20 }}
  />
);
